import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Paper,
  Button,
  Typography,
  Grid,
  Icon,
} from '@material-ui/core';
import get from 'lodash/get';
import { connect } from 'react-redux';
import resourcesActions from '../../redux/resource/ResourcesActions';

import DisplayResources from '../crud/DisplayResources';
import { resourceFields, orderStatusColors, rewardTypes } from '../../data/Constants';
import { commonResourceStyle, resourceFormStyle, viewResourceStyles } from '../../css/style';
import { titleCase, showTwoDecimalsForMoney } from '../../utils/Functions';
import LoadingBar from '../share/LoadingBar';
import { getAdmin } from '../../selectors';

const commonStyles = commonResourceStyle();
const formStyles = resourceFormStyle();
const viewStyles = viewResourceStyles();

const centeredItemsContainerStyle = { ...formStyles.gridContainer, ...commonStyles.centeredItemsContainer };

class OrderItems extends Component {
  componentDidMount = () => {
    const { resource, subResource, resourceId } = this.props.match.params;
    const {
      actions, loading, orders, apiToken,
    } = this.props;

    if (loading === false) {
      const queryParams = ['orders', resourceId, 'status'];
      if (!orders) {
        new Promise((resolve, reject) => {
          // The API currently requires both the start and count params in the request
          actions.getAllResources(apiToken, 'orders?start=0&count=10').then((res) => {
            if (res.error) return;
            actions.getAllResources(apiToken, ...queryParams, true).then((res) => {
              if (res.error) return;
              actions.getAllResources(apiToken, resource, resourceId, 'items');
              actions.getAllResources(apiToken, resource, resourceId, 'user');
            });
          });
          resolve();
        }).catch();
      } else {
        actions.getAllResources(apiToken, ...queryParams, true).then((res) => {
          if (res.error) return;
          actions.getAllResources(apiToken, resource, resourceId, 'items');
          actions.getAllResources(apiToken, resource, resourceId, 'user');
        });
      }
    }
  }

  componentDidUpdate = (prevProps) => {
    const {
      apiToken, history, match,
    } = this.props;
    const { resource } = match.params;
    if (prevProps.apiToken !== apiToken) {
      history.push('/orders',
        {
          fromOrderItems: true,
        });
    }
  }

  getOrderStatusStyle = orderStatus => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: orderStatusColors[orderStatus],
    lineHeight: 0.9,
    fontSize: '1rem',
  });

  prepareFieldValueForDisplay = (fieldKey, fieldValue) => {
    switch (fieldKey) {
      case 'isASAP':
        return fieldValue ? 'YES' : 'NO';
      case 'isCurbsidePickUp':
        return fieldValue ? 'YES' : null;
      case 'deliveryOption':
        return titleCase(fieldValue);
      case 'deliveryTrackingUrl':
        if (!fieldValue) return;
        return <a href={fieldValue}>Click here</a>;
      case 'orderStatus':
        if (!fieldValue) return;
        return (
          <div style={{ display: 'inline' }}>
            <div style={this.getOrderStatusStyle(fieldValue)}>
              <Icon style={commonStyles.smallerOrderStatusIcon}>lens</Icon>
              <Typography style={this.getOrderStatusStyle(fieldValue)}>{fieldValue}</Typography>
            </div>
          </div>
        );
      case 'desiredTime':
      case 'transactionTimestamp':
      case 'orderDate':
        if (Number.isInteger(fieldValue)) {
          // The API return dates as seconds since 1970. Convert first to milliseconds.
          return new Date(fieldValue * 1000).toString();
        }
        return;
      case 'coupons':
        if (!Array.isArray(fieldValue)) return '';
        const names = fieldValue.map(coupon => coupon.title);
        return names.join(', ');
      case 'subtotal':
      case 'tax':
      case 'couponValue':
      case 'totalPrice':
      case 'transactionAmount':
        const withTwoDecimals = showTwoDecimalsForMoney(fieldValue);
        return withTwoDecimals;
      case 'deliveryFee':
        const valueAsNumber = Number(fieldValue);
        if (valueAsNumber === 0) return null;
        if (valueAsNumber > 100) {
          return showTwoDecimalsForMoney(valueAsNumber / 100);
        }
        return showTwoDecimalsForMoney(valueAsNumber);
      default:
        return fieldValue;
    }
  }

  getAddressForDeliveryOption = (order) => {
    if (!order || !order.deliveryOption) return 'N/A';
    const { deliveryOption } = order;
    if (['PICKUP', 'DINE_IN'].includes(deliveryOption) && order.location && order.location.address) {
      return order.location.address;
    }
    if (['DELIVERY', 'CATERING'].includes(deliveryOption) && order.address) {
      const paths = ['unitNumber', 'streetAddress', 'city', 'province', 'postalCode', 'buzzerNumber'];
      const fullAddress = paths.map((path) => {
        if (path === 'buzzerNumber' && order.address[path]) {
          return `Buzz: ${order.address[path]}`;
        }
        const value = get(order.address, path, '');
        // Postal code is the last part of the address
        if (path === 'postalCode' && value) return value;
        if (!value) return '';
        return `${value}, `;
      });
      return fullAddress.join('');
    }
    return 'N/A';
  }

  getTransactionFieldTitleStyle = (fieldKey) => {
    if (fieldKey === 'subtotal') return viewStyles.subtotalPaddedGridItem;
    if (fieldKey === 'totalPrice') return viewStyles.paddedTransactionGridItem;
    return viewStyles.transactionGridItem;
  }

  getTransactionFieldValueStyle = (fieldKey) => {
    if (fieldKey === 'subtotal') return viewStyles.subtotalPaddedRightAlignGridItem;
    if (fieldKey === 'totalPrice') return viewStyles.paddedRightAlignTransactionGridItem;
    return viewStyles.rightAlignTransactionGridItem;
  }

  /**
   * Adds redeemed items, dollar rewards to the API's couponValue on the order
   */
  getCombinedCouponAndDiscountValue = (order) => {
    let orderCouponValue = get(order, 'couponValue', 0);

    const items = get(order, 'items', []);
    items.forEach((item) => {
      const redeemedAmount = get(item, 'totalRedeemedAmount', 0);
      orderCouponValue += redeemedAmount;
    });

    const coupons = get(order, 'coupons', []);
    coupons.forEach((coupon) => {
      const { rewardTiers: dollarRewardTier, actionValue, actionType } = coupon;
      if (!dollarRewardTier || actionType !== rewardTypes.DOLLAR) return;
      if (dollarRewardTier.rewardType !== rewardTypes.DOLLAR) return;

      orderCouponValue += actionValue;
    });

    return orderCouponValue;
  }

  getFieldValue = (order, field, path) => {
    if (!order) return null;
    return field.key === 'couponValue'
      ? this.getCombinedCouponAndDiscountValue(order)
      : get(order, path, field.emptyValue);
  }

  renderSection = (order, fields, section) => {
    const fieldValuesForDisplay = fields.map((field) => {
      const path = field.path ? field.path : field.key;
      let fieldValue = this.getFieldValue(order, field, path);
      if (field.key === 'address') {
        fieldValue = this.getAddressForDeliveryOption(order);
      }

      fieldValue = this.prepareFieldValueForDisplay(field.key, fieldValue);
      const deliveryOption = get(order, 'deliveryOption', '');
      const title = field.key === 'address' && deliveryOption !== 'DINE_IN' ? `${titleCase(deliveryOption)} Address` : field.title;

      if (!fieldValue) return null;
      if (section === 'top') {
        const normalValueComponent = <Typography style={commonStyles.inlineValue}>{fieldValue}</Typography>;
        const orderStatus = (
          <div style={commonStyles.orderStatusValueContainer}>
            {fieldValue}
          </div>
        );

        return (
          <Grid item xs={Number(field.gridWidth)} style={{ justifyContent: 'flex-start' }}>
            <Typography style={commonStyles.inlineTitle}>{`${title}: `}</Typography>
            {field.key === 'orderStatus' ? orderStatus : normalValueComponent}
          </Grid>
        );
      }

      const moneySectionTitleStyle = ['subtotal', 'totalPrice'].includes(field.key) ? commonStyles.underlinedInlineTitle : commonStyles.inlineTitle;

      if (section === 'bottom') {
        return (
          <Fragment>
            <Grid item xs={4} style={viewStyles.justifyFlexStartGridItem} />
            <Grid item xs={2} style={this.getTransactionFieldTitleStyle(field.key)}>
              <Typography style={moneySectionTitleStyle}>{`${field.title}: `}</Typography>
            </Grid>
            <Grid item xs={2} style={this.getTransactionFieldValueStyle(field.key)}>
              <Typography style={commonStyles.inlineValue}>{fieldValue}</Typography>
            </Grid>
            <Grid item xs={4} style={viewStyles.justifyFlexStartGridItem} />
          </Fragment>
        );
      }
    });

    let nullsRemoved = fieldValuesForDisplay.filter(components => components !== null);

    if (section === 'top') {
      if (nullsRemoved.length % 2 === 0) return nullsRemoved;
      const lastField = nullsRemoved[nullsRemoved.length - 1];
      const fullWidthLastField = (
        <Grid item xs={12}>
          {lastField}
        </Grid>
      );
      nullsRemoved[nullsRemoved.length - 1] = fullWidthLastField;
      return nullsRemoved;
    }
    if (section === 'bottom') return nullsRemoved;
  }

  reprintUserOrder(order) {
    const { adminToken, reprintOrder } = this.props;
    reprintOrder(order.user, adminToken, 'orders', order.id, 'reprint');
  }

  render() {
    const {
      orders, actions, loading, apiToken,
    } = this.props;
    const {
      resource, subResource, subResourceId, resourceId,
    } = this.props.match.params;
    const order = Array.isArray(orders) ? orders.find(order => order.id == resourceId) : null;
    const orderItems = order ? order.items : [];
    return (
      (loading === true)
        ? <LoadingBar />
        : (
          <div style={commonStyles.container}>
            <div style={commonStyles.resourceHeader}>
              <div style={commonStyles.resourceName}>
                <Typography style={commonStyles.resourceName}>Order Details</Typography>
              </div>
              {
                  order.location.cloudPrintEnabled
                  && (
                    <div style={commonStyles.reprintButtonContainer}>
                      <Button
                        onClick={() => this.reprintUserOrder(order)}
                        style={commonStyles.reprintButton}
                      >
                        <div style={commonStyles.reprintButtonText}>REPRINT ORDER</div>
                      </Button>
                    </div>
                  )
                }
            </div>
            <Paper style={{ ...commonStyles.paper, overflowX: 'hidden', overflowY: 'auto' }}>
              <Grid container spacing={24} style={centeredItemsContainerStyle}>
                <Grid item xs={12}>
                  <Typography style={viewStyles.title}>Customer Information</Typography>
                </Grid>
                {order && this.renderSection(order, resourceFields.orderItemsCustomerInformation, 'top')}
              </Grid>
              <Grid container spacing={24} style={centeredItemsContainerStyle}>
                <Grid item xs={12}>
                  <Typography style={viewStyles.title}>Order Details</Typography>
                </Grid>
                {order && this.renderSection(order, resourceFields.orderItemsOrderDetails, 'top')}
              </Grid>
              <DisplayResources data={orderItems} actions={actions} fieldNames={resourceFields.orderItems} resourceName="orderItems" viewOnly apiToken={apiToken} />
              <Grid container spacing={24} style={centeredItemsContainerStyle}>
                {order && this.renderSection(order, resourceFields.orderItemsBottomSection, 'bottom')}
              </Grid>
            </Paper>
          </div>
        )
    );
  }
}

const mapStateToProps = state => ({
  orders: state.getIn(['resources', 'orders']),
  adminToken: getAdmin(state).token,
});

const mapDispatchToProps = dispatch => ({
  reprintOrder: (user, adminToken, ...resourcePath) => dispatch(resourcesActions.reprintOrder(user, adminToken, ...resourcePath)),
});

OrderItems.propTypes = {
  reprintOrder: PropTypes.func.isRequired,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OrderItems));
